import { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Select,
  Button,
  Alert,
  Typography,
  Divider,
} from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { mappingApi } from '../../services/api/mapping';

const { Title, Text } = Typography;

const LOCALES = [
  { value: 'en_AU', label: 'English (Australia)' },
  { value: 'en_US', label: 'English (United States)' },
  { value: 'en_GB', label: 'English (United Kingdom)' },
  { value: 'en_NZ', label: 'English (New Zealand)' },
  { value: 'fr_FR', label: 'French (France)' },
  { value: 'de_DE', label: 'German (Germany)' },
  { value: 'es_ES', label: 'Spanish (Spain)' },
  { value: 'it_IT', label: 'Italian (Italy)' },
  { value: 'nl_NL', label: 'Dutch (Netherlands)' },
  { value: 'pt_BR', label: 'Portuguese (Brazil)' },
  { value: 'pt_PT', label: 'Portuguese (Portugal)' },
  { value: 'ja_JP', label: 'Japanese (Japan)' },
  { value: 'zh_CN', label: 'Chinese Simplified' },
  { value: 'zh_TW', label: 'Chinese Traditional' },
];

interface Props {
  mappingKey: string;
  tenantId?: string;
  templateData?: Record<string, any>;
  /** Live values from the parent — take priority over templateData so unsaved
   *  changes survive tab switches (the panel unmounts when not active). */
  liveValues?: Record<string, string | undefined>;
  onSaved?: () => void;
  /** Called on every change to a field so the wizard can reflect it immediately. */
  onFieldChange?: (field: string, value: string) => void;
}

export function AdvancedOptionsPanel({
  mappingKey,
  tenantId,
  templateData,
  liveValues,
  onSaved,
  onFieldChange,
}: Props) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Show address splitter when the template already has a value set
  const hasAddressField = !!templateData?.['splitAddressFunctionId'];

  // Pre-populate from templateData on mount; liveValues override so unsaved
  // changes survive tab switches (panel unmounts/remounts when tab changes).
  useEffect(() => {
    form.setFieldsValue({
      dateFormat:                liveValues?.dateFormat                ?? templateData?.['dateFormat']                ?? '',
      weekDateFormat:            liveValues?.weekDateFormat            ?? templateData?.['weekDateFormat']            ?? '',
      locale:                    liveValues?.locale                    ?? templateData?.['locale']                    ?? undefined,
      splitAddressFunctionId:    liveValues?.splitAddressFunctionId    ?? templateData?.['splitAddressFunctionId']    ?? undefined,
      lineItemsHeaderPattern:    liveValues?.lineItemsHeaderPattern    ?? templateData?.['lineItemsHeaderPattern']    ?? '',
      lineItemsFooterPattern:    liveValues?.lineItemsFooterPattern    ?? templateData?.['lineItemsFooterPattern']    ?? '',
    });
  }, [templateData, liveValues, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      setError(null);
      setSaved(false);

      const patch: Record<string, any> = {};
      if (values.dateFormat)               patch['dateFormat']               = values.dateFormat;
      if (values.weekDateFormat)           patch['weekDateFormat']           = values.weekDateFormat;
      if (values.locale)                   patch['locale']                   = values.locale;
      if (values.splitAddressFunctionId)   patch['splitAddressFunctionId']   = values.splitAddressFunctionId;
      if (values.lineItemsHeaderPattern)   patch['lineItemsHeaderPattern']   = values.lineItemsHeaderPattern;
      if (values.lineItemsFooterPattern)   patch['lineItemsFooterPattern']   = values.lineItemsFooterPattern;

      await mappingApi.updateTemplate(mappingKey, patch, tenantId);
      setSaved(true);
      onSaved?.();
    } catch (err: any) {
      if (err?.errorFields) return; // antd validation — shown inline
      if (err?.response?.status === 409) {
        setError('Rejected: changes would break previously verified data.');
      } else {
        setError(err?.response?.data?.message || err.message || 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Title level={5} style={{ marginTop: 0, marginBottom: 16 }}>
        Advanced Template Options
      </Title>

      {error && (
        <Alert type="error" message={error} showIcon closable
          onClose={() => setError(null)} style={{ marginBottom: 16 }} />
      )}
      {saved && (
        <Alert type="success" message="Saved successfully." showIcon closable
          onClose={() => setSaved(false)} style={{ marginBottom: 16 }} />
      )}

      <Form form={form} layout="vertical" size="small">
        <Divider plain style={{ fontSize: 12, color: '#888' }}>
          Date Formats
        </Divider>

        <Form.Item name="dateFormat" label={<Text style={{ fontSize: 13 }}>Date Format</Text>}
          tooltip="e.g. dd/MM/yyyy or MM/dd/yyyy">
          <Input
            placeholder="dd/MM/yyyy"
            style={{ width: 200 }}
            onChange={(e) => onFieldChange?.('dateFormat', e.target.value)}
          />
        </Form.Item>

        <Form.Item name="weekDateFormat" label={<Text style={{ fontSize: 13 }}>Week Date Format</Text>}
          tooltip="e.g. ww/yyyy">
          <Input placeholder="ww/yyyy" style={{ width: 200 }} />
        </Form.Item>

        <Divider plain style={{ fontSize: 12, color: '#888' }}>
          Locale
        </Divider>

        <Form.Item name="locale" label={<Text style={{ fontSize: 13 }}>Locale</Text>}
          tooltip="Affects number and date parsing (e.g. decimal separator , vs .)">
          <Select placeholder="Select locale" allowClear style={{ width: 280 }}
            options={LOCALES} showSearch
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        <Divider plain style={{ fontSize: 12, color: '#888' }}>
          Line Items Boundary
        </Divider>

        <Form.Item name="lineItemsHeaderPattern"
          label={<Text style={{ fontSize: 13 }}>Header Pattern</Text>}
          tooltip="Regex matching the line that marks the START of line items (e.g. ^Item.*Qty)">
          <Input
            placeholder="e.g. ^Item.*Qty"
            style={{ width: 320, fontFamily: 'monospace' }}
          />
        </Form.Item>

        <Form.Item name="lineItemsFooterPattern"
          label={<Text style={{ fontSize: 13 }}>Footer Pattern</Text>}
          tooltip="Regex matching the line that marks the END of line items (e.g. ^Sub[- ]?total)">
          <Input
            placeholder="e.g. ^Sub[- ]?total"
            style={{ width: 320, fontFamily: 'monospace' }}
          />
        </Form.Item>

        {hasAddressField && (
          <>
            <Divider plain style={{ fontSize: 12, color: '#888' }}>
              Address Splitting
            </Divider>
            <Form.Item name="splitAddressFunctionId"
              label={<Text style={{ fontSize: 13 }}>Address Splitter</Text>}
              tooltip="Custom function to split an address into parts">
              <Select placeholder="Select function" allowClear style={{ width: 280 }}
                options={[{ value: '_custom_', label: '_custom_' }]}
              />
            </Form.Item>
          </>
        )}
      </Form>

      <div style={{ marginTop: 24 }}>
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
          Save to Template
        </Button>
      </div>
    </div>
  );
}
